import React from 'react';
import { useTranslation } from 'react-i18next';
import { useForm, Controller } from 'react-hook-form';
import { useMutation } from 'react-query';
import {
  Box,
  Typography,
  Button,
  Paper,
  Divider,
} from '@mui/material';
import { MuiChipsInput } from 'mui-chips-input';
import SwalReact from 'sweetalert2-react-content';
import Swal from 'sweetalert2';
import { updateAccessControl } from 'services/transparency-config-service';
import { DotsLoader } from 'Components/DotsLoader';

const MySwal = SwalReact(Swal);

export const AccessControl = ({ config, onUpdate, companyKey }) => {
  const { t } = useTranslation(['transparency', 'common']);
  const { control, handleSubmit } = useForm({
    defaultValues: {
      allowedEmailDomains: config.allowedEmailDomains || [],
      allowedEmails: config.allowedEmails || [],
    },
  });

  const mutation = useMutation(
    (data) => updateAccessControl(companyKey, data),
    {
      onSuccess: () => {
        MySwal.fire({
          title: t('transparency:accessControl.notifications.success'),
          text: t('transparency:accessControl.notifications.successText'),
          icon: 'success',
        });
        onUpdate();
      },
      onError: (error) => {
        MySwal.fire({
          title: t('transparency:accessControl.notifications.error'),
          text: error.response?.data?.message || error.message,
          icon: 'error',
        });
      },
    }
  );

  const onSubmit = (data) => {
    mutation.mutate(data);
  };

  return (
    <Paper sx={{ p: 3 }}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Typography variant="h6" gutterBottom>
          {t('transparency:accessControl.title')}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {t('transparency:accessControl.subtitle')}
        </Typography>

        <Divider sx={{ my: 2 }} />

        {/* Allowed Domains */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="subtitle1" gutterBottom fontWeight={500}>
            {t('transparency:accessControl.domains.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('transparency:accessControl.domains.subtitle')}
          </Typography>
          <Controller
            control={control}
            name="allowedEmailDomains"
            render={({ field: { onChange, value } }) => (
              <MuiChipsInput
                value={value}
                onChange={onChange}
                placeholder={t('transparency:accessControl.domains.placeholder')}
                fullWidth
                renderChip={(Component, props) => (
                  <Component {...props} variant="outlined" color="primary" />
                )}
              />
            )}
          />
        </Box>

        {/* Allowed Emails (Exceptions) */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="subtitle1" gutterBottom fontWeight={500}>
            {t('transparency:accessControl.emails.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('transparency:accessControl.emails.subtitle')}
          </Typography>
          <Controller
            control={control}
            name="allowedEmails"
            render={({ field: { onChange, value } }) => (
              <MuiChipsInput
                value={value}
                onChange={onChange}
                placeholder={t('transparency:accessControl.emails.placeholder')}
                fullWidth
                renderChip={(Component, props) => (
                  <Component {...props} variant="outlined" color="secondary" />
                )}
              />
            )}
          />
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Submit */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
          <Button
            type="submit"
            variant="contained"
            disabled={mutation.isLoading}
          >
            {mutation.isLoading ? <DotsLoader /> : t('common:buttons.save')}
          </Button>
        </Box>
      </form>
    </Paper>
  );
};
